import { FC, ReactNode } from "react";

interface Props {
	label: string;
	value: ReactNode;
	previousValue?: number | null;
	currentValue?: number | null;
	loading?: boolean;
	error?: boolean;
	onRetry?: () => void;
	hint?: ReactNode;
	footer?: ReactNode;
	emptyText?: string;
}

const formatDelta = (current: number, previous: number) => {
	if (previous === 0) {
		return current === 0 ? "0%" : "—";
	}
	const ratio = (current - previous) / previous;
	const sign = ratio > 0 ? "+" : "";
	return `${sign}${(ratio * 100).toFixed(1)}%`;
};

const KpiCard: FC<Props> = ({
	label,
	value,
	previousValue,
	currentValue,
	loading,
	error,
	onRetry,
	hint,
	footer,
	emptyText
}) => {
	const showDelta =
		typeof currentValue === "number" &&
		typeof previousValue === "number" &&
		!loading &&
		!error;
	const ratio =
		showDelta && previousValue !== 0
			? (currentValue - previousValue) / previousValue
			: 0;
	const deltaTone =
		ratio > 0
			? "text-emerald-700"
			: ratio < 0
				? "text-rose-700"
				: "text-muted-foreground";
	// Non-colour redundant cue: an arrow glyph + a screen-reader-only
	// word so that colour-blind users and assistive tech both get the
	// direction. WCAG 1.4.1 (Use of Colour). The colour shades above are
	// also bumped to 700 to keep contrast safely above 4.5:1 on bg-subtle.
	const deltaIcon = ratio > 0 ? "▲" : ratio < 0 ? "▼" : "•";
	const deltaSrLabel =
		ratio > 0 ? "increase" : ratio < 0 ? "decrease" : "no change";

	return (
		<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
			<p className="text-sm text-muted-foreground mb-1">{label}</p>
			{loading ? (
				<div
					className="h-8 w-24 rounded animate-pulse bg-zinc-200"
					aria-label="Loading"
				/>
			) : error ? (
				<div className="text-sm text-rose-600">
					Failed to load.
					{onRetry ? (
						<button type="button" onClick={onRetry} className="ml-2 underline">
							Retry
						</button>
					) : null}
				</div>
			) : value === null || value === undefined || value === "" ? (
				<p className="text-sm text-muted-foreground">
					{emptyText ?? "No data yet"}
				</p>
			) : (
				<p className="text-2xl font-heading font-semibold text-foreground">
					{value}
				</p>
			)}
			{showDelta ? (
				<p className={`text-xs mt-1 ${deltaTone}`}>
					<span aria-hidden="true">{deltaIcon}</span>{" "}
					<span className="sr-only">{deltaSrLabel}: </span>
					{formatDelta(currentValue, previousValue)} vs previous
				</p>
			) : null}
			{hint ? (
				<p className="text-xs text-muted-foreground mt-1">{hint}</p>
			) : null}
			{footer ? <div className="mt-3">{footer}</div> : null}
		</div>
	);
};

export default KpiCard;
